import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BigNumber, ethers } from 'ethers'

import { NETWORK_LIST, getAvailableProviderWithChainId, getChainIdFromShortName } from '../../utils/config'
import {
    connectWallet,
    disconnectWallet,
    getWalletSnapshot,
    restoreWallet,
    subscribeWallet,
} from '../../utils/walletProviders'
import { MARKETPLACE_ABI, MARKETPLACE_ERC20_ABI, MARKETPLACE_ERC721_ABI, MARKETPLACE_FACTORY_ABI } from './marketplaceAbi'
import {
    fetchMetadata,
    listingFromResult,
    loadMarketplaceOwnedTokenIds,
    loadTokenMetadataPage,
    normalizeMetadataUrl,
    saleFromResult,
} from './marketplaceData'
import {
    PAGE_SIZE,
    expiryFromPreset,
    normalizeListingInput,
    percentToBps,
    shouldBlockMarketLoad,
} from './marketplaceModel'
import { marketplaceMulticall } from './marketplaceMulticall'
import { createMigrationValidationError, validateMigrationTarget } from './marketplaceMigration'
import { accumulateFloorPrice, accumulateRecentVolume, shouldContinueSalesScan } from './marketplaceStats'
import { errorMessage, executeListingBatches } from './marketplaceTransactions'

const ZERO_ADDRESS = ethers.constants.AddressZero

function getExplorer(network) {
    if (network?.blockExplorerUrls?.[0]) return network.blockExplorerUrls[0].replace(/\/$/, '')
    if (Number(network?.chainId) === 97) return 'https://testnet.bscscan.com'
    if (Number(network?.chainId) === 56) return 'https://bscscan.com'
    return ''
}

function emptyPage(page = 1) {
    return { page, total: 0, items: [], metadata: {}, loading: false, error: '' }
}

async function loadFloorFromContractPages(market, total, currencyAddress) {
    let floorPrice = null
    for (let offset = 0; offset < total; offset += 50) {
        const page = await market.getActiveListingsPage(offset, Math.min(50, total - offset))
        const listings = page.map(listingFromResult)
        floorPrice = accumulateFloorPrice(floorPrice, listings, currencyAddress)
    }
    return floorPrice
}

async function loadVolume24hFromContractPages(market, total, currencyAddress) {
    const cutoff = Math.floor(Date.now() / 1000) - 24 * 60 * 60
    let volume = BigNumber.from(0)
    let remaining = total
    while (remaining > 0) {
        const offset = Math.max(0, remaining - 50)
        const [page] = await market.getSalesPage(offset, remaining - offset)
        const sales = page.map(saleFromResult)
        volume = accumulateRecentVolume(volume, sales, currencyAddress, cutoff)
        if (!shouldContinueSalesScan(sales, cutoff)) break
        remaining = offset
    }
    return volume
}

export function useMarketplace({ chain, marketAddress }) {
    const chainId = getChainIdFromShortName(chain)
    const network = useMemo(
        () => NETWORK_LIST.find((item) => Number(item.chainId) === Number(chainId)),
        [chainId]
    )
    const [wallet, setWallet] = useState(getWalletSnapshot())
    const [core, setCore] = useState(null)
    const [project, setProject] = useState({})
    const [projectLoading, setProjectLoading] = useState(true)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [listings, setListings] = useState(emptyPage())
    const [owned, setOwned] = useState(emptyPage())
    const [sellerListings, setSellerListings] = useState(emptyPage())
    const [purchases, setPurchases] = useState(emptyPage())
    const [sales, setSales] = useState(emptyPage())
    const [activity, setActivity] = useState(emptyPage())
    const [toast, setToast] = useState(null)
    const [transactionSuccess, setTransactionSuccess] = useState(null)
    const runtimeRef = useRef({})
    const metadataCacheRef = useRef(new Map())
    const ownedIdsCacheRef = useRef({ key: '', tokenIds: [], strategy: '' })
    const requestRef = useRef(0)
    const projectMarketRef = useRef('')
    const projectMetadataUriRef = useRef('')

    const showToast = useCallback((message, type = 'success', details = {}) => {
        setToast({ id: Date.now(), message, type, ...details })
    }, [])

    useEffect(() => {
        const unsubscribe = subscribeWallet(setWallet)
        restoreWallet()
        return unsubscribe
    }, [])

    const loadMetadataForIds = useCallback(async (nft, tokenIds) => {
        const ids = tokenIds.map((value) => BigNumber.from(value).toString())
        const missing = ids.filter((id) => !metadataCacheRef.current.has(id))
        if (missing.length) {
            const loaded = await loadTokenMetadataPage(nft, missing, { concurrency: 4 })
            loaded.forEach((entry) => metadataCacheRef.current.set(entry.id, entry))
        }
        return Object.fromEntries(ids.map((id) => [id, metadataCacheRef.current.get(id)]))
    }, [])

    const getReadProvider = useCallback(async () => {
        if (!network) throw new Error(`Unsupported chain: ${chain}`)
        if (wallet.provider && Number(wallet.chainId) === Number(chainId)) {
            return new ethers.providers.Web3Provider(wallet.provider, 'any')
        }
        return getAvailableProviderWithChainId(chainId)
    }, [chain, chainId, network, wallet.chainId, wallet.provider])

    const loadCore = useCallback(async () => {
        const requestId = ++requestRef.current
        const normalizedMarketAddress = String(marketAddress || '').toLowerCase()
        const nextMarketKey = `${chainId}:${normalizedMarketAddress}`
        const blockingLoad = shouldBlockMarketLoad({
            activeKey: projectMarketRef.current,
            nextKey: nextMarketKey,
            hasRuntime: Boolean(runtimeRef.current.market),
        })
        if (projectMarketRef.current !== nextMarketKey) {
            projectMarketRef.current = nextMarketKey
            projectMetadataUriRef.current = ''
            runtimeRef.current = {}
            setCore(null)
            setProject({})
            setProjectLoading(true)
        }
        if (blockingLoad) setLoading(true)
        setError('')
        try {
            if (!network) throw new Error(`Unsupported chain route: ${chain}`)
            if (!ethers.utils.isAddress(marketAddress)) throw new Error('Invalid marketplace address')
            const provider = await getReadProvider()
            const code = await provider.getCode(marketAddress)
            if (code === '0x') throw new Error('Marketplace contract not found on this network')

            const market = new ethers.Contract(marketAddress, MARKETPLACE_ABI, provider)
            const marketInterface = new ethers.utils.Interface(MARKETPLACE_ABI)
            const nftInterface = new ethers.utils.Interface(MARKETPLACE_ERC721_ABI)
            const factoryInterface = new ethers.utils.Interface(MARKETPLACE_FACTORY_ABI)
            const tokenInterface = new ethers.utils.Interface(MARKETPLACE_ERC20_ABI)
            const [
                factoryAddress, nftAddress, ownerAddress, pendingOwner, version, settlementAddress, projectFeeBps,
                projectFeeRecipient, royaltyMode, customRoyaltyBps, customRoyaltyRecipient,
                metadataURI, paused, retired, migrationActive, migrationStarted, migrationTarget,
                migrationCursor, activeCount, totalSales,
            ] = await marketplaceMulticall(provider, [
                'factory', 'nft', 'owner', 'pendingOwner', 'marketVersion', 'defaultSettlementToken', 'projectFeeBps',
                'projectFeeRecipient', 'royaltyMode', 'customRoyaltyBps', 'customRoyaltyRecipient',
                'metadataURI', 'paused', 'retired', 'migrationActive', 'migrationStarted',
                'migrationTarget', 'migrationCursor', 'activeListingCount', 'totalSales',
            ].map((functionName) => ({ target: marketAddress, iface: marketInterface, functionName })))

            const nft = new ethers.Contract(nftAddress, MARKETPLACE_ERC721_ABI, provider)
            const factory = new ethers.Contract(factoryAddress, MARKETPLACE_FACTORY_ABI, provider)
            const secondaryCalls = [
                { target: nftAddress, iface: nftInterface, functionName: 'name' },
                { target: nftAddress, iface: nftInterface, functionName: 'symbol' },
                { target: nftAddress, iface: nftInterface, functionName: 'owner', fallback: '' },
                { target: factoryAddress, iface: factoryInterface, functionName: 'platformFeeBps' },
                { target: factoryAddress, iface: factoryInterface, functionName: 'platformFeeRecipient' },
                { target: marketAddress, iface: marketInterface, functionName: 'totalVolume', args: [settlementAddress] },
            ]
            if (settlementAddress !== ZERO_ADDRESS) {
                secondaryCalls.push(
                    { target: settlementAddress, iface: tokenInterface, functionName: 'name' },
                    { target: settlementAddress, iface: tokenInterface, functionName: 'symbol' },
                    { target: settlementAddress, iface: tokenInterface, functionName: 'decimals' }
                )
            }
            const [nftName, nftSymbol, nftOwner, platformFeeBps, platformFeeRecipient, totalVolume, tokenName, tokenSymbol, tokenDecimals] = await marketplaceMulticall(provider, secondaryCalls)
            const currency = settlementAddress === ZERO_ADDRESS
                ? { address: ZERO_ADDRESS, name: network.nativeCurrency.name, symbol: network.nativeCurrency.symbol, decimals: Number(network.nativeCurrency.decimals || 18), native: true }
                : { address: settlementAddress, name: tokenName, symbol: tokenSymbol, decimals: Number(tokenDecimals), native: false }
            if (requestId !== requestRef.current) return

            const nextCore = {
                marketAddress: ethers.utils.getAddress(marketAddress), factoryAddress, nftAddress,
                owner: ownerAddress, pendingOwner, nftOwner, version: Number(version), settlementAddress,
                currency, projectFeeBps: Number(projectFeeBps), projectFeeRecipient,
                platformFeeBps: Number(platformFeeBps), platformFeeRecipient,
                royaltyMode: Number(royaltyMode), customRoyaltyBps: Number(customRoyaltyBps),
                customRoyaltyRecipient, metadataURI, paused, retired, migrationActive,
                migrationStarted, migrationTarget, migrationCursor: BigNumber.from(migrationCursor),
                activeCount: Number(activeCount), totalSales: Number(totalSales), totalVolume,
                floorPrice: null, volume24h: null, nftName, nftSymbol,
                official: Boolean(nftOwner && nftOwner.toLowerCase() === ownerAddress.toLowerCase()),
            }
            if (runtimeRef.current.nftAddress?.toLowerCase() !== nftAddress.toLowerCase()) {
                metadataCacheRef.current.clear()
                ownedIdsCacheRef.current = { key: '', tokenIds: [], strategy: '' }
            }
            runtimeRef.current = { provider, market, nft, factory, nftAddress }
            setCore(nextCore)
            setLoading(false)

            if (Number(activeCount) > 0) {
                loadFloorFromContractPages(market, Number(activeCount), settlementAddress)
                    .then((floorPrice) => {
                        if (requestId === requestRef.current) {
                            setCore((current) => current ? { ...current, floorPrice } : current)
                        }
                    })
                    .catch(() => {})
            }
            if (Number(totalSales) > 0) {
                loadVolume24hFromContractPages(market, Number(totalSales), settlementAddress)
                    .then((volume24h) => {
                        if (requestId === requestRef.current) {
                            setCore((current) => current ? { ...current, volume24h } : current)
                        }
                    })
                    .catch(() => {})
            } else {
                setCore((current) => current ? { ...current, volume24h: BigNumber.from(0) } : current)
            }

            if (metadataURI) {
                const nextMetadataURI = String(metadataURI)
                if (projectMetadataUriRef.current !== nextMetadataURI) {
                    projectMetadataUriRef.current = nextMetadataURI
                    setProjectLoading(true)
                }
                fetchMetadata(metadataURI)
                    .then((metadata) => {
                        if (requestId !== requestRef.current) return
                        setProject(metadata)
                        document.title = `${metadata.name || nftName} · FatSale Market`
                        const favicon = normalizeMetadataUrl(metadata.logo || metadata.image || '')
                        if (favicon) document.querySelector('link[rel="icon"]')?.setAttribute('href', favicon)
                    })
                    .catch(() => {
                        if (requestId === requestRef.current) setProject({})
                    })
                    .finally(() => {
                        if (requestId === requestRef.current) setProjectLoading(false)
                    })
            } else {
                projectMetadataUriRef.current = ''
                setProject({})
                setProjectLoading(false)
                document.title = `${nftName} · FatSale Market`
            }
        } catch (loadError) {
            if (requestId !== requestRef.current) return
            if (blockingLoad) {
                setError(errorMessage(loadError))
                setLoading(false)
                setProjectLoading(false)
            } else {
                showToast('marketRefreshFailed', 'error')
            }
        }
    }, [chain, chainId, getReadProvider, marketAddress, network, showToast])

    useEffect(() => {
        loadCore()
    }, [loadCore])

    const loadListings = useCallback(async (page = 1) => {
        const { market, nft } = runtimeRef.current
        if (!market || !nft || !core) return
        setListings((current) => ({ ...current, page, loading: true, error: '' }))
        try {
            const raw = await market.getActiveListingsPage((page - 1) * PAGE_SIZE, PAGE_SIZE)
            const items = raw.map(listingFromResult)
            const metadata = await loadMetadataForIds(nft, items.map((item) => item.tokenId))
            setListings({ page, total: core.activeCount, items, metadata, loading: false, error: '' })
        } catch (loadError) {
            setListings((current) => ({ ...current, loading: false, error: errorMessage(loadError) }))
        }
    }, [core, loadMetadataForIds])

    const invalidateOwnedCache = useCallback(() => {
        ownedIdsCacheRef.current = { key: '', tokenIds: [], strategy: '' }
    }, [])

    const loadOwned = useCallback(async (page = 1, options = {}) => {
        const { nft, nftAddress } = runtimeRef.current
        if (!nft || !wallet.account) {
            setOwned(emptyPage(page))
            return
        }
        setOwned((current) => ({ ...current, page, loading: true, error: '' }))
        try {
            const cacheKey = `${String(nftAddress || '').toLowerCase()}:${wallet.account.toLowerCase()}`
            let cached = ownedIdsCacheRef.current
            if (options.force || cached.key !== cacheKey) {
                const result = await loadMarketplaceOwnedTokenIds(nft, wallet.account)
                cached = { key: cacheKey, tokenIds: result.tokenIds, strategy: result.strategy }
                ownedIdsCacheRef.current = cached
            }
            const allIds = cached.tokenIds
            const pageIds = allIds.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
            const metadata = await loadMetadataForIds(nft, pageIds)
            setOwned({ page, total: allIds.length, allIds, strategy: cached.strategy, items: pageIds, metadata, loading: false, error: '' })
        } catch (loadError) {
            setOwned((current) => ({ ...current, loading: false, error: errorMessage(loadError) }))
        }
    }, [loadMetadataForIds, wallet.account])

    const loadHistoryPage = useCallback(async (kind, page) => {
        const { market, nft } = runtimeRef.current
        if (!market || !nft) return
        const setters = { sellerListings: setSellerListings, purchases: setPurchases, sales: setSales, activity: setActivity }
        const setter = setters[kind]
        setter((current) => ({ ...current, page, loading: true, error: '' }))
        try {
            let result
            if (kind === 'sellerListings') {
                if (!wallet.account) return setter(emptyPage(page))
                result = await market.getSellerListingsPage(wallet.account, (page - 1) * PAGE_SIZE, PAGE_SIZE)
                const items = result[0].map(listingFromResult)
                const metadata = await loadMetadataForIds(nft, items.map((item) => item.tokenId))
                return setter({ page, total: Number(result[1]), items, metadata, loading: false, error: '' })
            }
            if (kind === 'purchases') {
                if (!wallet.account) return setter(emptyPage(page))
                result = await market.getBuyerPurchasesPage(wallet.account, (page - 1) * PAGE_SIZE, PAGE_SIZE)
            } else if (kind === 'sales') {
                if (!wallet.account) return setter(emptyPage(page))
                result = await market.getSellerSalesPage(wallet.account, (page - 1) * PAGE_SIZE, PAGE_SIZE)
            } else {
                result = await market.getSalesPage((page - 1) * PAGE_SIZE, PAGE_SIZE)
            }
            const items = result[0].map(saleFromResult)
            const metadata = await loadMetadataForIds(nft, items.map((item) => item.tokenId))
            setter({ page, total: Number(result[1]), items, metadata, loading: false, error: '' })
        } catch (loadError) {
            setter((current) => ({ ...current, loading: false, error: errorMessage(loadError) }))
        }
    }, [loadMetadataForIds, wallet.account])

    const loadListingDetail = useCallback(async (tokenId) => {
        const { market, nft } = runtimeRef.current
        if (!market || !nft) throw new Error('Marketplace is not ready')
        const listing = listingFromResult(await market.getListingByTokenId(tokenId))
        const metadata = await loadMetadataForIds(nft, [tokenId])
        return { listing, metadata: metadata[String(tokenId)] }
    }, [loadMetadataForIds])

    useEffect(() => {
        if (core) loadListings(1)
    }, [core, loadListings])

    useEffect(() => {
        if (core && wallet.account) loadOwned(1)
    }, [core, loadOwned, wallet.account])

    const ensureSigner = useCallback(async () => {
        if (!wallet.account || !wallet.provider) throw new Error('walletRequired')
        if (Number(wallet.chainId) !== Number(chainId)) throw new Error('networkRequired')
        const provider = new ethers.providers.Web3Provider(wallet.provider, 'any')
        const signer = provider.getSigner(wallet.account)
        return {
            provider,
            signer,
            market: new ethers.Contract(marketAddress, MARKETPLACE_ABI, signer),
            nft: new ethers.Contract(core.nftAddress, MARKETPLACE_ERC721_ABI, signer),
        }
    }, [chainId, core, marketAddress, wallet.account, wallet.chainId, wallet.provider])

    const switchNetwork = useCallback(async () => {
        if (!wallet.provider || !network) throw new Error('walletRequired')
        const hexChainId = ethers.utils.hexValue(network.chainId)
        try {
            await wallet.provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hexChainId }] })
        } catch (switchError) {
            if (switchError.code !== 4902) throw switchError
            await wallet.provider.request({
                method: 'wallet_addEthereumChain',
                params: [{
                    chainId: hexChainId,
                    chainName: network.name,
                    nativeCurrency: network.nativeCurrency,
                    rpcUrls: network.rpcUrls,
                    blockExplorerUrls: network.blockExplorerUrls || [getExplorer(network)].filter(Boolean),
                }],
            })
        }
    }, [network, wallet.provider])

    const connect = useCallback((walletId) => connectWallet(walletId), [])
    const disconnect = useCallback(() => disconnectWallet(), [])

    const waitFor = useCallback(async (transaction, successMessage, options = {}) => {
        const resultMode = options.resultMode || 'dialog'
        showToast('transactionSubmitted', 'info', { txHash: transaction.hash })
        const receipt = await transaction.wait()
        setToast(null)
        if (resultMode === 'dialog') {
            setTransactionSuccess({ id: `${transaction.hash}-${Date.now()}`, message: successMessage, txHash: transaction.hash })
        } else if (resultMode === 'toast') {
            showToast(successMessage, 'success', { txHash: transaction.hash })
        }
        return receipt
    }, [showToast])

    const listTokens = useCallback(async ({ tokenIds, priceByToken, uniformPrice, expiryPreset, onProgress }) => {
        const { market, nft } = await ensureSigner()
        const approved = await nft.isApprovedForAll(wallet.account, marketAddress)
        if (!approved) {
            const approval = await nft.setApprovalForAll(marketAddress, true)
            await waitFor(approval, 'operationSuccess', { resultMode: 'toast' })
        }
        const inputs = tokenIds.map((tokenId) => normalizeListingInput({
            tokenId,
            price: priceByToken?.[String(tokenId)] || uniformPrice,
            decimals: core.currency.decimals,
            expiryPreset,
        }))
        const result = await executeListingBatches({
            listings: inputs,
            onProgress,
            submitBatch: async (batch) => {
                await market.callStatic.createListings(batch)
                const transaction = await market.createListings(batch)
                return waitFor(transaction, 'listingSuccess', { resultMode: 'silent' })
            },
            submitSingle: async (listing) => {
                await market.callStatic.createListing(listing.tokenId, listing.price, listing.expiresAt)
                const transaction = await market.createListing(listing.tokenId, listing.price, listing.expiresAt)
                return waitFor(transaction, 'listingSuccess', { resultMode: 'silent' })
            },
        })
        invalidateOwnedCache()
        await Promise.all([loadCore(), loadOwned(1, { force: true })])
        return result
    }, [core, ensureSigner, invalidateOwnedCache, loadCore, loadOwned, marketAddress, waitFor, wallet.account])

    const buyListing = useCallback(async (listing) => {
        const { market, signer } = await ensureSigner()
        if (listing.currency !== ZERO_ADDRESS) {
            const token = new ethers.Contract(listing.currency, MARKETPLACE_ERC20_ABI, signer)
            const allowance = await token.allowance(wallet.account, marketAddress)
            if (allowance.lt(listing.price)) {
                const approval = await token.approve(marketAddress, listing.price)
                await waitFor(approval, 'operationSuccess', { resultMode: 'toast' })
            }
        }
        const overrides = listing.currency === ZERO_ADDRESS ? { value: listing.price } : {}
        await market.callStatic.buy(listing.tokenId, overrides)
        const transaction = await market.buy(listing.tokenId, overrides)
        const receipt = await waitFor(transaction, 'purchaseSuccess')
        invalidateOwnedCache()
        await Promise.all([loadCore(), loadOwned(1, { force: true }), loadHistoryPage('purchases', 1)])
        return receipt
    }, [ensureSigner, invalidateOwnedCache, loadCore, loadHistoryPage, loadOwned, marketAddress, waitFor, wallet.account])

    const cancelListing = useCallback(async (listing) => {
        const { market } = await ensureSigner()
        await market.callStatic.cancelListing(listing.tokenId)
        const receipt = await waitFor(await market.cancelListing(listing.tokenId), 'cancellationSuccess')
        invalidateOwnedCache()
        await Promise.all([loadCore(), loadOwned(1, { force: true }), loadHistoryPage('sellerListings', 1)])
        return receipt
    }, [ensureSigner, invalidateOwnedCache, loadCore, loadHistoryPage, loadOwned, waitFor])

    const updateListing = useCallback(async (listing, price, expiryPreset) => {
        const { market } = await ensureSigner()
        const rawPrice = ethers.utils.parseUnits(String(price), core.currency.decimals)
        const expiresAt = expiryFromPreset(expiryPreset)
        await market.callStatic.updateListing(listing.tokenId, rawPrice, expiresAt)
        const receipt = await waitFor(await market.updateListing(listing.tokenId, rawPrice, expiresAt), 'priceUpdateSuccess')
        await Promise.all([loadCore(), loadHistoryPage('sellerListings', 1)])
        return receipt
    }, [core, ensureSigner, loadCore, loadHistoryPage, waitFor])

    const runAdmin = useCallback(async (method, args, successMessage = 'operationSuccess') => {
        const { market } = await ensureSigner()
        await market.callStatic[method](...args)
        const receipt = await waitFor(await market[method](...args), successMessage)
        await loadCore()
        return receipt
    }, [ensureSigner, loadCore, waitFor])

    const saveSettlement = useCallback((address) => {
        if (address !== ZERO_ADDRESS && !ethers.utils.isAddress(address)) throw new Error('invalidAddress')
        return runAdmin('setDefaultSettlementToken', [address])
    }, [runAdmin])
    const inspectSettlementToken = useCallback(async (address) => {
        if (!ethers.utils.isAddress(address) || address === ZERO_ADDRESS) throw new Error('invalidAddress')
        const provider = await getReadProvider()
        const target = ethers.utils.getAddress(address)
        const tokenInterface = new ethers.utils.Interface(MARKETPLACE_ERC20_ABI)
        const [name, symbol, decimals] = await marketplaceMulticall(provider, [
            { target, iface: tokenInterface, functionName: 'name' },
            { target, iface: tokenInterface, functionName: 'symbol' },
            { target, iface: tokenInterface, functionName: 'decimals' },
        ])
        return { address: target, name: String(name), symbol: String(symbol), decimals: Number(decimals) }
    }, [getReadProvider])
    const inspectMigrationTarget = useCallback(async (address) => {
        if (!ethers.utils.isAddress(address)) throw new Error('invalidAddress')
        const provider = await getReadProvider()
        const targetAddress = ethers.utils.getAddress(address)
        if (await provider.getCode(targetAddress) === '0x') {
            throw createMigrationValidationError('migrationTargetNotContract')
        }

        const marketInterface = new ethers.utils.Interface(MARKETPLACE_ABI)
        const factoryInterface = new ethers.utils.Interface(MARKETPLACE_FACTORY_ABI)
        let result
        try {
            result = await marketplaceMulticall(provider, [
                { target: targetAddress, iface: marketInterface, functionName: 'factory' },
                { target: targetAddress, iface: marketInterface, functionName: 'nft' },
                { target: targetAddress, iface: marketInterface, functionName: 'owner' },
                { target: targetAddress, iface: marketInterface, functionName: 'marketVersion' },
                {
                    target: core.factoryAddress,
                    iface: factoryInterface,
                    functionName: 'isMigrationApproved',
                    args: [marketAddress, targetAddress],
                },
            ])
        } catch (error) {
            throw createMigrationValidationError('migrationTargetInvalidMarket')
        }

        const target = {
            marketAddress: targetAddress,
            factoryAddress: result[0],
            nftAddress: result[1],
            owner: result[2],
            version: Number(result[3]),
        }
        validateMigrationTarget(core, target, Boolean(result[4]))
        return target
    }, [core, getReadProvider, marketAddress])
    const saveProjectFee = useCallback((percent, recipient) => {
        if (!ethers.utils.isAddress(recipient)) throw new Error('invalidAddress')
        return runAdmin('setProjectFee', [percentToBps(percent), recipient])
    }, [runAdmin])
    const saveRoyalty = useCallback((mode, percent, recipient) => {
        const bps = Number(mode) === 2 ? percentToBps(percent) : 0
        const target = Number(mode) === 2 ? recipient : ZERO_ADDRESS
        if (Number(mode) === 2 && !ethers.utils.isAddress(target)) throw new Error('invalidAddress')
        return runAdmin('setRoyaltyConfig', [Number(mode), bps, target])
    }, [runAdmin])

    const copyValue = useCallback(async (value) => {
        try {
            await navigator.clipboard.writeText(value)
            showToast('copied')
        } catch (copyError) {
            showToast('copyFailed', 'error')
        }
    }, [showToast])

    return {
        chainId, network, explorer: getExplorer(network), wallet, core, project, projectLoading, loading, error,
        listings, owned, sellerListings, purchases, sales, activity, toast, setToast,
        transactionSuccess, setTransactionSuccess,
        connect, disconnect, switchNetwork, reload: loadCore, loadListings, loadOwned, loadHistoryPage, loadListingDetail,
        listTokens, buyListing, cancelListing, updateListing, runAdmin, saveSettlement, saveProjectFee,
        saveRoyalty, inspectSettlementToken, inspectMigrationTarget, copyValue, showToast,
    }
}
